import { useState } from 'react';

import { ensureError, isDefined, isEmpty, isNotDefined } from '@enterprise/core';
import { useDidMount } from '@enterprise/hooks';

import type { HasAccessProps } from '#has-access/types/has-access.props';

import { usePermissions } from '#context/permissions/hooks/permissions.hook';

import type { HasAccessState } from './types/has-access-state.type';

/**
 * Custom hook to determine access configuration based on permissions and roles.
 *
 * @param permissions - List of permissions to check against.
 * @param permissionsStrategy - Strategy for evaluating permissions ('any' or 'all').
 * @param roles - List of roles to check against.
 * @param rolesStrategy - Strategy for evaluating roles ('any' or 'all').
 * @returns An object containing:
 *   - `error`: Any error encountered during authorization check.
 *   - `hasAccess`: Whether the user has access based on the provided configuration.
 *   - `isError`: Boolean indicating if an error occurred.
 *   - `isLoading`: Boolean indicating if the authorization check is in progress.
 *
 * @remarks
 * This hook uses the `usePermissions` context to evaluate access and handles loading and error states.
 */
export const useHasAccessConfiguration = ({
  permissions,
  permissionsStrategy = 'any',
  roles,
  rolesStrategy = 'any',
}: Pick<HasAccessProps, 'permissions' | 'permissionsStrategy' | 'roles' | 'rolesStrategy'>) => {
  const noCheckNeeded = (isNotDefined(permissions) && isNotDefined(roles)) || (isEmpty(permissions) && isEmpty(roles));

  const [state, setState] = useState<HasAccessState>({
    error: undefined,
    hasAccess: noCheckNeeded,
    isLoading: !noCheckNeeded,
  });

  const { isAuthorized } = usePermissions();

  useDidMount(() => {
    if ((isNotDefined(permissions) && isNotDefined(roles)) || (isEmpty(permissions) && isEmpty(roles))) {
      setState({ error: undefined, hasAccess: true, isLoading: false });

      return;
    }

    isAuthorized({ permissions: permissions ?? [], permissionsStrategy, roles: roles ?? [], rolesStrategy })
      .then((allowed) => {
        setState({ error: undefined, hasAccess: allowed, isLoading: false });
      })
      .catch((e: unknown) => {
        setState({ error: ensureError(e), hasAccess: false, isLoading: false });
      });
  });

  return {
    error: state.error,
    hasAccess: state.hasAccess,
    isError: isDefined(state.error),
    isLoading: state.isLoading,
  };
};
